import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Icon, type IconName } from './icons';

/**
 * The shared page frame.
 *
 * Every screen previously chose its own `max-w-*` and padding, so the audit log was wider
 * than the team page for no reason and headings sat at four different sizes. One wrapper
 * makes that consistent by construction rather than by everyone remembering.
 */
export function Page({
  children, width = 'normal', className,
}: {
  children: ReactNode;
  /** normal for forms and settings, wide for tables that need the room. */
  width?: 'normal' | 'wide' | 'full';
  className?: string;
}) {
  const widths = {
    normal: 'max-w-3xl',
    wide: 'max-w-5xl',
    full: 'max-w-none',
  };

  return (
    <div className={clsx('mx-auto w-full px-6 py-7', widths[width], className)}>
      {children}
    </div>
  );
}

/**
 * Title, one line of context, and the actions for the page.
 *
 * The description is not decoration: these screens administer things with consequences —
 * removing a member, requiring two-factor — and a sentence saying what the page does is
 * what stops somebody guessing.
 */
export function PageHeader({
  title, description, icon, actions,
}: {
  title: string;
  description?: ReactNode;
  icon?: IconName;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="flex items-center gap-2.5 text-[22px] font-semibold leading-tight
                       tracking-[-0.01em]">
          {icon && (
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg
                             bg-[var(--ff-surface-sunken)] text-[var(--ff-text-muted)]">
              <Icon name={icon} size={17} />
            </span>
          )}
          {title}
        </h1>
        {description && (
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-[var(--ff-text-muted)]">
            {description}
          </p>
        )}
      </div>

      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/**
 * A titled block. Sections carry their own heading and optional hint so a page reads as a
 * sequence of decisions rather than an undifferentiated stack of cards.
 */
export function Section({
  title, hint, actions, children, tone = 'default',
}: {
  title?: string;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** danger draws a section whose actions are hard to undo. */
  tone?: 'default' | 'danger';
}) {
  return (
    <section
      className={clsx(
        'rounded-xl border bg-[var(--ff-surface)]',
        tone === 'danger'
          ? 'border-[var(--ff-danger)]/35'
          : 'border-[var(--ff-border)]',
      )}
    >
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b
                        border-[var(--ff-border)] px-5 py-3.5">
          <div className="min-w-0">
            {title && (
              <h2 className={clsx(
                'text-sm font-semibold',
                tone === 'danger' && 'text-[var(--ff-danger)]',
              )}>
                {title}
              </h2>
            )}
            {hint && (
              <p className="mt-1 max-w-xl text-xs leading-relaxed text-[var(--ff-text-muted)]">
                {hint}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}

      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

/** Vertical rhythm between sections, so no page invents its own spacing. */
export function Stack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('space-y-5', className)}>{children}</div>;
}

/**
 * A table that scrolls inside itself rather than pushing the page sideways.
 *
 * Horizontal page scroll is the single most common way an admin table breaks on a laptop,
 * and it is invisible until somebody with a narrower screen opens it.
 */
export function DataTable({
  headers, children, empty,
}: {
  headers: ReactNode[];
  children: ReactNode;
  empty?: ReactNode;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--ff-border)]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--ff-border)] bg-[var(--ff-surface-sunken)]">
            {headers.map((header, i) => (
              <th
                key={i}
                className="px-4 py-2.5 text-left text-xs font-semibold uppercase
                           tracking-wide text-[var(--ff-text-muted)]"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>

      {empty}
    </div>
  );
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <tr className={clsx(
      'border-b border-[var(--ff-border)] last:border-0',
      'transition-colors hover:bg-[var(--ff-surface-raised)]',
      className,
    )}>
      {children}
    </tr>
  );
}

export function Cell({
  children, className, muted, nowrap,
}: {
  children: ReactNode;
  className?: string;
  muted?: boolean;
  nowrap?: boolean;
}) {
  return (
    <td className={clsx(
      'px-4 py-3 align-middle',
      muted && 'text-[var(--ff-text-muted)]',
      nowrap && 'whitespace-nowrap',
      className,
    )}>
      {children}
    </td>
  );
}

/**
 * A short statement of fact at the top of a page — counts, status, provider.
 *
 * Deliberately not a chart. These are administrative screens where the useful number is
 * "3 members, 1 invitation pending", and a sparkline for that would be decoration.
 */
export function StatRow({ items }: { items: { label: string; value: ReactNode; tone?: 'default' | 'warning' }[] }) {
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-xl border border-[var(--ff-border)] bg-[var(--ff-surface)] px-4 py-3"
        >
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--ff-text-muted)]">
            {item.label}
          </div>
          <div className={clsx(
            'mt-1 text-xl font-semibold tabular-nums',
            item.tone === 'warning' && 'text-[var(--ff-warning)]',
          )}>
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** An inline note. Used for "no email provider configured" and similar. */
export function Notice({
  tone = 'info', children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-[var(--ff-border)] bg-[var(--ff-surface-sunken)] text-[var(--ff-text-muted)]',
    warning: 'border-[var(--ff-warning)]/30 bg-[var(--ff-warning-soft)] text-[var(--ff-warning)]',
    danger: 'border-[var(--ff-danger)]/30 bg-[var(--ff-danger-soft)] text-[var(--ff-danger)]',
    success: 'border-[var(--ff-success)]/30 bg-[var(--ff-success-soft)] text-[var(--ff-success)]',
  };

  const icons: Record<typeof tone, IconName> = {
    info: 'info', warning: 'alert', danger: 'alert', success: 'check',
  };

  return (
    <div className={clsx(
      'flex gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm leading-relaxed',
      tones[tone],
    )}>
      <Icon name={icons[tone]} size={15} className="mt-0.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
